/**
 * When to ask about push, and when to stop asking — V7-01 §2.1.
 *
 * Pure functions over an explicit state object, with `now` passed in. The
 * cadence rules are the sort of thing that is trivially wrong and invisibly so
 * — a banner that reappears every render is indistinguishable from one that
 * works until somebody complains — and V7-00's protocol requires anything
 * touching time-of-day logic to pin its clock. Nothing here reads `Date.now()`
 * or `localStorage`; that wiring lives in `PushEnrollmentBanner.tsx`, which is
 * the component's whole job.
 *
 * The honest constraint §2 records: every browser requires a user gesture and a
 * per-browser grant. "Enabled automatically with no user action ever" is not
 * achievable by design, so what ships is *ask once, well, and then never
 * again* — this file is the "never again" half.
 */

/** Seven days, §2.1's snooze. */
export const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

/** Third dismissal stops it permanently. */
export const MAX_DISMISSALS = 3;

export interface BannerState {
  /** How many times "Not now" has been used. */
  dismissCount: number;
  /** Epoch ms before which the banner stays down. */
  snoozedUntil: number | null;
  /** Set by "Don't ask again" or by the third dismissal. Terminal. */
  stopped: boolean;
  /** The session that last saw it — "once per session" is per browser tab run. */
  lastShownSession: string | null;
}

export const INITIAL_BANNER_STATE: BannerState = {
  dismissCount: 0,
  snoozedUntil: null,
  stopped: false,
  lastShownSession: null,
};

export interface BannerContext {
  state: BannerState;
  now: number;
  /** Distinguishes one tab-run from the next; `sessionStorage`-backed. */
  sessionId: string;
  supported: boolean;
  isGuest: boolean;
  permission: NotificationPermission;
  /** Whether this browser already has a live subscription registered. */
  hasSubscription: boolean;
  /**
   * Whether this person still wants push at all.
   *
   * §2.1 says the banner is for "anyone with push events enabled in prefs".
   * Read literally that would show it to nobody: every push column in
   * `DEFAULT_NOTIFICATION_MATRIX` ships `false`, and the rows are only turned
   * on when a device is first registered — which is the thing the banner
   * exists to cause. So the gate is the *intent* of that sentence rather than
   * its letter: do not nag somebody who has switched push off. Someone who has
   * never had a device has not made that choice yet.
   */
  pushPreferenceOn: boolean;
}

/**
 * Has this person turned push off, having once had it on?
 *
 * The only reading of "enabled in prefs" that does not gate the banner to
 * nobody. A matrix with no push row ticked means one of two very different
 * things — "never asked" or "asked and declined" — and only the second is a
 * reason to stay quiet. Having registered a device at some point is what tells
 * them apart.
 */
export function wantsPush(input: {
  anyPushRowOn: boolean;
  hasEverRegisteredADevice: boolean;
}): boolean {
  if (input.anyPushRowOn) return true;
  return !input.hasEverRegisteredADevice;
}

/**
 * Whether to show the enrollment banner right now.
 *
 * Every `false` below is deliberate rather than defensive:
 *
 *  - **unsupported / guest** — §2.1 says show nothing at all, not a disabled
 *    control explaining a thing they cannot do.
 *  - **`denied`** — the prompt will not reappear however many times it is
 *    clicked; the diagnostics panel is where a blocked site gets its
 *    per-browser instructions, and a banner offering a button that does
 *    nothing is worse than silence.
 *  - **`granted`** — either they are subscribed, or `ensureSubscribed()` is
 *    already fixing it silently (§2.2). Neither needs a banner.
 *  - **preference off** — they turned push off in the matrix. Asking anyway
 *    would be overriding a choice they already made.
 */
export function shouldShowBanner(context: BannerContext): boolean {
  const { state } = context;

  if (!context.supported || context.isGuest) return false;
  if (state.stopped) return false;
  if (!context.pushPreferenceOn) return false;
  if (context.permission !== 'default') return false;
  if (context.hasSubscription) return false;
  if (state.snoozedUntil !== null && context.now < state.snoozedUntil) return false;
  if (state.lastShownSession === context.sessionId) return false;

  return true;
}

/** Records that this session has seen it — the "once per session" half. */
export function markShown(state: BannerState, sessionId: string): BannerState {
  return { ...state, lastShownSession: sessionId };
}

/**
 * "Not now": down for seven days, and permanently after the third one.
 *
 * The count is what makes this humane. Three refusals is a decision, and a
 * product that keeps asking after a decision is one people learn to dismiss
 * without reading — which costs the prompts that actually matter.
 */
export function dismissBanner(state: BannerState, now: number): BannerState {
  const dismissCount = state.dismissCount + 1;
  return {
    ...state,
    dismissCount,
    snoozedUntil: now + SNOOZE_MS,
    stopped: dismissCount >= MAX_DISMISSALS,
  };
}

/** "Don't ask again": terminal, immediately. */
export function stopAsking(state: BannerState): BannerState {
  return { ...state, stopped: true };
}

/**
 * Successful enrollment.
 *
 * Also terminal: there is nothing left to ask for on this browser, and
 * resetting the counters would mean an unsubscribe six months later reopened
 * the whole cadence from zero.
 */
export function enrolled(state: BannerState): BannerState {
  return { ...state, stopped: true };
}

/** Tolerates anything in storage, including a shape from a previous version. */
export function parseBannerState(raw: string | null): BannerState {
  if (!raw) return INITIAL_BANNER_STATE;
  try {
    const parsed = JSON.parse(raw) as Partial<BannerState>;
    return {
      dismissCount: Number.isFinite(parsed.dismissCount) ? Number(parsed.dismissCount) : 0,
      snoozedUntil: Number.isFinite(parsed.snoozedUntil) ? Number(parsed.snoozedUntil) : null,
      stopped: parsed.stopped === true,
      lastShownSession: typeof parsed.lastShownSession === 'string' ? parsed.lastShownSession : null,
    };
  } catch {
    return INITIAL_BANNER_STATE;
  }
}
